import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import toast from 'react-hot-toast';
import {
  getContractWithDetails,
  completeContract,
  cancelContract,
  disputeContract,
  approveMilestone,
  rejectMilestone,
  approveTimeEntry,
} from '../../../services/contractService';
import ReviewModal from '../../../components/ReviewModal';
import TipModal from '../../../components/TipModal';
import { hasReviewedContract } from '../../../services/reviewService';
import { hasTippedContract } from '../../../services/tipService';

// ── Static maps ───────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  offered:        'bg-info/10 text-info border-info/30',
  accepted:       'bg-success/10 text-success border-success/30',
  in_progress:    'bg-primary/10 text-primary border-primary/30',
  pending_review: 'bg-warning/10 text-warning border-warning/30',
  completed:      'bg-success/10 text-success border-success/30',
  cancelled:      'bg-destructive/10 text-destructive border-destructive/30',
  disputed:       'bg-warning/10 text-warning border-warning/30',
};
const STATUS_LABELS = {
  offered: 'New Offer', accepted: 'Accepted', in_progress: 'In Progress',
  pending_review: 'Pending Review', completed: 'Completed', cancelled: 'Cancelled', disputed: 'Disputed',
};
const MILESTONE_CHIP = {
  pending:     'bg-muted text-muted-foreground',
  in_progress: 'bg-primary/10 text-primary',
  submitted:   'bg-warning/10 text-warning',
  approved:    'bg-success/10 text-success',
  rejected:    'bg-destructive/10 text-destructive',
};

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 p-3 bg-background border border-border rounded-lg">
    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
      <Icon name={icon} size={15} className="text-muted-foreground" />
    </div>
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold text-foreground">{value}</p>
    </div>
  </div>
);

const EmptyState = ({ icon, message }) => (
  <div className="text-center py-14">
    <Icon name={icon} className="w-14 h-14 text-muted-foreground mx-auto mb-3 opacity-40" />
    <p className="text-muted-foreground text-sm">{message}</p>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
const ClientContractModal = ({ contract: init, onClose, onRefresh }) => {
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);   // milestone id being rejected
  const [rejectReason, setRejectReason] = useState('');

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showDisputeConfirm, setShowDisputeConfirm] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [showPartialConfirm, setShowPartialConfirm] = useState(false);
  const [partialAmountInput, setPartialAmountInput] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [alreadyTipped, setAlreadyTipped] = useState(false);

  const tabs = [
    { id: 'overview',   label: 'Overview',   icon: 'FileText'    },
    ...(init?.contract_type === 'milestone' ? [{ id: 'milestones', label: 'Milestones', icon: 'CheckSquare' }] : []),
    ...(init?.contract_type === 'hourly'    ? [{ id: 'timesheet',  label: 'Timesheet',  icon: 'Clock'      }] : []),
    { id: 'payments',   label: 'Payments',   icon: 'DollarSign'  },
  ];

  useEffect(() => { loadDetails(); }, [init?.id]);
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const loadDetails = async () => {
    setLoading(true);
    const result = await getContractWithDetails(init?.id);
    setContract(result?.success ? result.contract : init);
    if (result?.success && result.contract?.status === 'completed') {
      const [reviewed, tipped] = await Promise.all([
        hasReviewedContract(init?.id),
        hasTippedContract(init?.id),
      ]);
      setAlreadyReviewed(reviewed);
      setAlreadyTipped(tipped);
    }
    setLoading(false);
    if (!result?.success) toast.error('Failed to load contract details');
  };

  const doAction = async (fn, label, args = []) => {
    setActionLoading(label);
    try {
      const r = await fn(...args);
      if (!r?.success) throw new Error(r?.error);
      toast.success(`${label} successful`);
      await loadDetails();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.message || `${label} failed`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelContract = async () => {
    if (!cancelReason.trim()) return toast.error('Please provide a reason for cancellation');
    setActionLoading('Cancel');
    try {
      const hadEscrow = c?.escrow_status === 'held';
      const result = await cancelContract(c.id, cancelReason.trim());
      if (!result?.success) throw new Error(result?.error);
      toast.success(hadEscrow ? 'Contract cancelled — escrow released to the expert in full' : 'Contract cancelled');
      setShowCancelConfirm(false);
      setCancelReason('');
      await loadDetails();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to cancel contract');
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveMilestone = (id) => doAction(approveMilestone, 'Approve milestone', [id]);
  const handleApproveTime      = (id) => doAction(approveTimeEntry, 'Approve time entry', [id]);

  const handleCompleteContract = async () => {
    const isHourly          = c?.contract_type === 'hourly';
    const isFixedOrMilestone = c?.contract_type === 'fixed' || c?.contract_type === 'milestone';

    if (c?.escrow_status === 'held' && !showPartialConfirm) {
      // For hourly: require all time entries to be resolved first
      if (isHourly) {
        const pendingCount = timeEntries.filter(e => e.status === 'pending').length;
        if (pendingCount > 0) {
          return toast.error(
            `Please approve or reject all ${pendingCount} pending time entr${pendingCount === 1 ? 'y' : 'ies'} before completing the contract`
          );
        }
        // Pre-fill with sum of approved entries; client can pay more (up to cap) but not less
        setPartialAmountInput(approvedBill.toFixed(2));
        setShowPartialConfirm(true);
        return;
      }
      // For fixed/milestone: show the amount dialog pre-filled with full escrow
      if (isFixedOrMilestone) {
        const defaultAmt = (c?.escrow_amount ?? c?.budget_cap ?? '').toString();
        setPartialAmountInput(defaultAmt);
        setShowPartialConfirm(true);
        return;
      }
    }

    // Validate entered amount before proceeding
    let approvedAmount;
    if (c?.escrow_status === 'held' && (isHourly || isFixedOrMilestone)) {
      const parsed = parseFloat(partialAmountInput);
      if (isNaN(parsed) || parsed <= 0) {
        return toast.error('Please enter a valid approved amount greater than $0');
      }
      const cap = c?.escrow_amount ?? c?.budget_cap;
      if (cap && parsed > cap) {
        return toast.error(`Approved amount cannot exceed the escrowed amount ($${cap.toFixed(2)})`);
      }
      // Hourly: cannot pay less than what was already approved
      if (isHourly && parsed < approvedBill) {
        return toast.error(`Amount must be at least the approved time total ($${approvedBill.toFixed(2)})`);
      }
      approvedAmount = parsed;
    }

    setActionLoading('Complete');
    try {
      const r = await completeContract(c.id, approvedAmount);
      if (!r?.success) throw new Error(r?.error);
      const refundMsg = approvedAmount != null && approvedAmount < (c?.escrow_amount ?? approvedAmount)
        ? ` $${(c.escrow_amount - approvedAmount).toFixed(2)} refunded to you.`
        : '';
      toast.success(`Contract completed — escrow released to expert.${refundMsg}`);
      setShowPartialConfirm(false);
      setPartialAmountInput('');
      setAlreadyReviewed(false); // reset so review modal can show
      setAlreadyTipped(false);    // reset so tip modal can show after review
      await loadDetails();
      onRefresh?.();
      // Prompt the client to leave a review
      setShowReviewModal(true);
    } catch (err) {
      toast.error(err?.message || 'Complete failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectMilestone = async () => {
    if (!rejectReason.trim()) return toast.error('Please provide a rejection reason');
    await doAction(rejectMilestone, 'Reject milestone', [rejectingId, rejectReason]);
    setRejectingId(null);
    setRejectReason('');
  };


  if (!init) return null;

  const c = contract || init;
  const milestones  = [...(c.milestones   || [])].sort((a, b) => a.display_order - b.display_order);
  const timeEntries = [...(c.time_entries || [])].sort((a, b) => new Date(b.work_date) - new Date(a.work_date));

  const approvedMs      = milestones.filter(m => m.status === 'approved').length;
  const submittedMs     = milestones.filter(m => m.status === 'submitted').length;
  const progressPct     = milestones.length > 0 ? Math.round((approvedMs / milestones.length) * 100) : 0;
  const approvedMsValue = milestones.filter(m => m.status === 'approved').reduce((s, m) => s + (m.amount || 0), 0);

  const totalHours   = (timeEntries.reduce((s, e) => s + (e.minutes || 0), 0) / 60).toFixed(1);
  const totalBill    = timeEntries.reduce((s, e) => s + (e.billable_amount || 0), 0);
  const approvedBill = timeEntries.filter(e => e.status === 'approved').reduce((s, e) => s + (e.billable_amount || 0), 0);
  const pendingBill  = timeEntries.filter(e => e.status === 'pending').reduce((s, e) => s + (e.billable_amount || 0), 0);

  const canAct = !['completed', 'cancelled', 'disputed'].includes(c.status);
  const pendingActions = submittedMs + timeEntries.filter(e => e.status === 'pending').length;

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card border border-border rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">

        {/* ── Header ── */}
        <div className="flex items-start justify-between p-5 md:p-6 border-b border-border flex-shrink-0">
          <div className="flex-1 min-w-0 mr-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-bold text-foreground">{c.title}</h2>
              <span className={`px-2.5 py-1 text-xs font-semibold rounded border flex-shrink-0 ${STATUS_COLORS[c.status] || 'bg-muted text-muted-foreground border-border'}`}>
                {STATUS_LABELS[c.status] || c.status}
              </span>
              {pendingActions > 0 && (
                <span className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-full bg-warning text-white flex-shrink-0">
                  <Icon name="Bell" size={11} />
                  {pendingActions} action{pendingActions > 1 ? 's' : ''} needed
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              #{c.contract_number} · {c.contract_type === 'hourly' ? 'Hourly' : 'Milestone'} Contract
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {c.expert?.user_id && (
              <Button
                variant="outline"
                size="sm"
                iconName="MessageSquare"
                iconPosition="left"
                onClick={() => navigate(`/client-dashboard/messages?userId=${c.expert.user_id}`)}
              >
                Message Expert
              </Button>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex border-b border-border flex-shrink-0 overflow-x-auto scrollbar-none">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab.icon} size={15} />
              {tab.label}
              {tab.id === 'milestones' && submittedMs > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 text-xs rounded-full bg-warning text-white font-bold">{submittedMs}</span>
              )}

            </button>
          ))}
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader" className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* ════════════ OVERVIEW ════════════ */}
              {activeTab === 'overview' && (
                <div className="space-y-5">
                  {/* Expert card */}
                  {c.expert && (
                    <div className="flex items-center gap-4 p-4 bg-background border border-border rounded-xl">
                      <div className="w-11 h-11 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="UserCheck" size={20} className="text-secondary" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-foreground">{c.expert?.full_name || 'Expert'}</p>
                        <p className="text-sm text-muted-foreground">Assigned Expert</p>
                      </div>
                      {c.expert?.hourly_rate && (
                        <div className="text-right">
                          <p className="text-sm font-bold text-foreground">${c.expert.hourly_rate}/hr</p>
                          <p className="text-xs text-muted-foreground">Rate</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Details grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {c.contract_type === 'hourly' && (
                      <>
                        <InfoRow icon="DollarSign" label="Hourly Rate"  value={`$${c.hourly_rate?.toFixed(2)}/hr`} />
                        <InfoRow icon="Target"     label="Budget Cap"   value={c.budget_cap ? `$${c.budget_cap?.toFixed(2)}` : 'No cap'} />
                      </>
                    )}
                    {c.contract_type === 'milestone' && (
                      <>
                        <InfoRow icon="DollarSign"  label="Total Value"   value={`$${c.total_contract_value?.toFixed(2)}`} />
                        <InfoRow icon="CheckSquare" label="Milestones"    value={`${approvedMs} / ${milestones.length} approved`} />
                      </>
                    )}
                    {c.deadline && (
                      <InfoRow icon="Calendar"   label="Deadline"     value={new Date(c.deadline).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} />
                    )}
                    {c.start_date && (
                      <InfoRow icon="PlayCircle" label="Start Date"   value={new Date(c.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} />
                    )}
                    <InfoRow icon="Hash"        label="Contract #"   value={c.contract_number} />
                    <InfoRow icon="CreditCard"  label="Escrow"       value={c.escrow_status || 'none'} />
                  </div>

                  {c.description && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Description</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed bg-muted/40 rounded-lg p-4">{c.description}</p>
                    </div>
                  )}
                  {c.deliverables && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-2">Deliverables</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed bg-muted/40 rounded-lg p-4 whitespace-pre-wrap">{c.deliverables}</p>
                    </div>
                  )}

                  {/* Timeline */}
                  {(c.created_at || c.accepted_at || c.completed_at) && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-3">Timeline</h4>
                      <div className="space-y-2">
                        {[
                          { label: 'Created',   ts: c.created_at,   icon: 'FileText',   color: 'text-muted-foreground' },
                          { label: 'Accepted',  ts: c.accepted_at,  icon: 'CheckCircle',color: 'text-success' },
                          { label: 'Started',   ts: c.start_date,   icon: 'Play',       color: 'text-primary' },
                          { label: 'Completed', ts: c.completed_at, icon: 'Trophy',     color: 'text-success' },
                          { label: 'Cancelled', ts: c.cancelled_at, icon: 'XCircle',    color: 'text-destructive' },
                        ].filter(e => e.ts).map((ev) => (
                          <div key={ev.label} className="flex items-center gap-3 text-sm">
                            <Icon name={ev.icon} size={15} className={ev.color} />
                            <span className="text-muted-foreground w-20">{ev.label}</span>
                            <span className="text-foreground font-medium">
                              {new Date(ev.ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Leave a review (completed contracts) */}
                  {c.status === 'completed' && c.expert && (
                    <div className="pt-3 border-t border-border">
                      <div className="p-4 bg-success/5 border border-success/20 rounded-xl">
                        <div className="flex items-start gap-3">
                          <Icon name="Star" size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-foreground mb-1">Contract Completed</p>
                            <p className="text-xs text-muted-foreground mb-3">
                              How was your experience with {c.expert?.full_name || 'this expert'}? Leave a review to help other clients.
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                              {alreadyReviewed ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-success/10 text-success border border-success/30">
                                  <Icon name="CheckCircle" size={13} />
                                  Review Submitted
                                </span>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setShowReviewModal(true)}
                                  iconName="Star"
                                  iconPosition="left"
                                >
                                  Leave a Review
                                </Button>
                              )}
                              {alreadyTipped ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-500/10 text-amber-600 border border-amber-400/30">
                                  <Icon name="Heart" size={13} />
                                  Tip Sent
                                </span>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setShowTipModal(true)}
                                  iconName="Heart"
                                  iconPosition="left"
                                  className="text-amber-600 border-amber-400/50 hover:bg-amber-500/10"
                                >
                                  Send a Tip
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Client actions */}
                  {canAct && (
                    <div className="pt-3 border-t border-border space-y-3">
                      {c.status === 'in_progress' && (
                        <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                          <div className="flex items-start gap-3">
                            <Icon name="Clock" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-foreground mb-1">Work in progress</p>
                              <p className="text-xs text-muted-foreground">
                                The expert is working on your contract. You'll be able to approve and release payment once they submit their work for review.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {c.status === 'pending_review' && (
                        <div className="p-4 bg-warning/5 border border-warning/20 rounded-xl">
                          <div className="flex items-start gap-3">
                            <Icon name="CheckCircle" size={18} className="text-warning flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-foreground mb-1">Expert submitted work for review</p>
                              <p className="text-xs text-muted-foreground mb-3">
                                Review the deliverables and approve the work to mark the contract as complete and release escrow to the expert.
                              </p>

                              {/* Hourly: warn if time entries still need review */}
                              {c.contract_type === 'hourly' && !showPartialConfirm && (() => {
                                const pendingCount = timeEntries.filter(e => e.status === 'pending').length;
                                return pendingCount > 0 ? (
                                  <div className="mb-3 p-3 bg-warning/10 border border-warning/30 rounded-lg flex items-start gap-2">
                                    <Icon name="AlertTriangle" size={14} className="text-warning flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-warning font-medium">
                                      {pendingCount} time entr{pendingCount === 1 ? 'y needs' : 'ies need'} your approval or rejection before you can complete this contract.
                                      <button
                                        className="underline ml-1"
                                        onClick={() => setActiveTab('timesheet')}
                                      >Review timesheet →</button>
                                    </p>
                                  </div>
                                ) : null;
                              })()}

                              {showPartialConfirm ? (
                                <div className="p-3 bg-muted/40 rounded-lg border border-border space-y-3">
                                  <p className="text-sm font-medium text-foreground">
                                    How much should be released to the expert?
                                  </p>
                                  {c.contract_type === 'hourly' ? (
                                    <div className="space-y-1">
                                      <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>Approved time total</span>
                                        <span className="font-semibold text-success">${approvedBill.toFixed(2)}</span>
                                      </div>
                                      <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>Escrowed (budget cap)</span>
                                        <span>${(c?.escrow_amount ?? c?.budget_cap ?? 0).toFixed(2)}</span>
                                      </div>
                                      <p className="text-xs text-muted-foreground pt-1">
                                        Enter an amount ≥ approved total. Any unused escrow will be refunded to you.
                                      </p>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-muted-foreground">
                                      Escrowed: <strong>${(c?.escrow_amount ?? c?.budget_cap ?? 0).toFixed(2)}</strong>. Any difference will be refunded to your payment method.
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">$</span>
                                    <input
                                      type="number"
                                      min={c.contract_type === 'hourly' ? approvedBill : 0.01}
                                      step="0.01"
                                      max={c?.escrow_amount ?? c?.budget_cap}
                                      value={partialAmountInput}
                                      onChange={(e) => setPartialAmountInput(e.target.value)}
                                      className="w-36 px-3 py-1.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                                      placeholder="0.00"
                                    />
                                    {c.contract_type === 'hourly' && parseFloat(partialAmountInput) < approvedBill && (
                                      <span className="text-xs text-destructive">Min ${approvedBill.toFixed(2)}</span>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => handleCompleteContract()}
                                      disabled={!!actionLoading}
                                    >
                                      {actionLoading === 'Complete' ? 'Completing...' : 'Confirm & Release'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => { setShowPartialConfirm(false); setPartialAmountInput(''); }}
                                      disabled={!!actionLoading}
                                    >
                                      Back
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  onClick={() => handleCompleteContract()}
                                  disabled={!!actionLoading}
                                  iconName="Trophy"
                                  iconPosition="left"
                                >
                                  {actionLoading === 'Complete' ? 'Completing...' : 'Approve & Complete'}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {!showCancelConfirm ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive border-destructive/30 hover:bg-destructive/10"
                          onClick={() => setShowCancelConfirm(true)}
                          iconName="XCircle"
                          iconPosition="left"
                        >
                          Cancel Contract
                        </Button>
                      ) : (
                        <div className="p-3 bg-destructive/10 rounded-lg border border-destructive/20 space-y-2">
                          <div className="flex items-center gap-2">
                            <Icon name="AlertTriangle" size={16} className="text-destructive flex-shrink-0" />
                            <p className="text-sm text-destructive font-medium">Cancel this contract? This cannot be undone.</p>
                          </div>
                          <textarea
                            rows={2}
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Reason for cancellation (required)…"
                            className="w-full px-3 py-2 text-sm bg-background border border-destructive/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-destructive/40 resize-none"
                            maxLength={500}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive border-destructive/30"
                              onClick={() => handleCancelContract()}
                              disabled={!!actionLoading || !cancelReason.trim()}
                            >
                              {actionLoading === 'Cancel' ? 'Cancelling...' : 'Yes, Cancel'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setShowCancelConfirm(false); setCancelReason(''); }}>No</Button>
                          </div>
                        </div>
                      )}

                      {canAct && !showDisputeConfirm && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-warning border-warning/30 hover:bg-warning/10"
                          onClick={() => setShowDisputeConfirm(true)}
                          iconName="AlertTriangle"
                          iconPosition="left"
                        >
                          File a Dispute
                        </Button>
                      )}
                      {canAct && showDisputeConfirm && (
                        <div className="p-3 bg-warning/10 rounded-lg border border-warning/20 space-y-2">
                          <div className="flex items-center gap-2">
                            <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0" />
                            <p className="text-sm text-warning font-medium">File a dispute? Admins will be notified immediately.</p>
                          </div>
                          <textarea
                            rows={3}
                            value={disputeReason}
                            onChange={(e) => setDisputeReason(e.target.value)}
                            placeholder="Describe your dispute in detail (required)…"
                            className="w-full px-3 py-2 text-sm bg-background border border-warning/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-warning/40 resize-none"
                            maxLength={1000}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-warning border-warning/30"
                              onClick={() => handleDisputeContract()}
                              disabled={!!actionLoading || !disputeReason.trim()}
                            >
                              {actionLoading === 'Dispute' ? 'Filing...' : 'Submit Dispute'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setShowDisputeConfirm(false); setDisputeReason(''); }}>Cancel</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ════════════ MILESTONES ════════════ */}
              {activeTab === 'milestones' && (
                <div className="space-y-5">
                  {milestones.length === 0 ? (
                    <EmptyState icon="CheckSquare" message="No milestones defined for this contract." />
                  ) : (
                    <>
                      {/* Progress */}
                      <div className="bg-background border border-border rounded-xl p-5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-foreground">Overall Progress</span>
                          <span className="text-sm font-bold text-primary">{approvedMs}/{milestones.length} approved</span>
                        </div>
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-700" style={{ width: `${progressPct}%` }} />
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2 flex-wrap">
                          {[
                            { label: 'Pending',   color: 'bg-muted',       count: milestones.filter(m => m.status === 'pending').length    },
                            { label: 'Submitted', color: 'bg-warning',     count: submittedMs                                               },
                            { label: 'Approved',  color: 'bg-success',     count: approvedMs                                               },
                            { label: 'Rejected',  color: 'bg-destructive', count: milestones.filter(m => m.status === 'rejected').length   },
                          ].filter(s => s.count > 0).map(s => (
                            <span key={s.label} className="flex items-center gap-1">
                              <span className={`w-2 h-2 rounded-full ${s.color}`} />
                              {s.count} {s.label}
                            </span>
                          ))}
                          <span className="ml-auto font-bold text-primary">{progressPct}%</span>
                        </div>
                      </div>

                      {/* Rejection reason inline form */}
                      {rejectingId && (
                        <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-xl space-y-3">
                          <p className="text-sm font-semibold text-destructive flex items-center gap-2">
                            <Icon name="AlertTriangle" size={15} />
                            Rejection reason
                          </p>
                          <textarea
                            className="w-full px-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                            rows={3}
                            placeholder="Explain why this milestone is being rejected so the expert can correct it..."
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="text-destructive border-destructive/30" onClick={handleRejectMilestone} disabled={!!actionLoading}>
                              {actionLoading === 'Reject milestone' ? 'Rejecting...' : 'Confirm Rejection'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setRejectingId(null); setRejectReason(''); }}>Cancel</Button>
                          </div>
                        </div>
                      )}

                      {/* Milestone cards */}
                      <div className="space-y-3">
                        {milestones.map((ms, i) => (
                          <div key={ms.id} className={`bg-background border rounded-xl p-4 transition-colors ${
                            ms.status === 'approved'  ? 'border-success/30 bg-success/5' :
                            ms.status === 'submitted' ? 'border-warning/30 bg-warning/5' :
                            ms.status === 'rejected'  ? 'border-destructive/30'           :
                            'border-border'
                          }`}>
                            <div className="flex items-start gap-4">
                              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                                ms.status === 'approved'  ? 'bg-success text-white'     :
                                ms.status === 'submitted' ? 'bg-warning text-white'     :
                                ms.status === 'rejected'  ? 'bg-destructive text-white' :
                                'bg-muted text-muted-foreground'
                              }`}>
                                {ms.status === 'approved' ? <Icon name="Check" size={16} /> : i + 1}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 flex-wrap mb-1">
                                  <p className="font-semibold text-foreground">{ms.title}</p>
                                  <span className={`px-2.5 py-0.5 text-xs rounded-full font-medium flex-shrink-0 ${MILESTONE_CHIP[ms.status] || 'bg-muted text-muted-foreground'}`}>
                                    {ms.status?.replace('_', ' ')}
                                  </span>
                                </div>
                                {ms.description && <p className="text-sm text-muted-foreground mb-2">{ms.description}</p>}

                                <div className="flex flex-wrap items-center gap-4 text-sm">
                                  <span className="font-bold text-foreground text-base">${ms.amount?.toFixed(2)}</span>
                                  {ms.due_date && (
                                    <span className={`flex items-center gap-1 text-xs ${new Date(ms.due_date) < new Date() && ms.status !== 'approved' ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                                      <Icon name="Calendar" size={12} />
                                      Due {new Date(ms.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                  )}
                                  {ms.submitted_at && (
                                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                      <Icon name="Send" size={12} />
                                      Submitted {new Date(ms.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </span>
                                  )}
                                </div>

                                {ms.rejection_reason && (
                                  <div className="mt-2.5 px-3 py-2 bg-destructive/10 rounded-lg border border-destructive/20">
                                    <p className="text-xs text-destructive font-medium">
                                      <Icon name="AlertCircle" size={12} className="inline mr-1" />
                                      Rejected: {ms.rejection_reason}
                                    </p>
                                  </div>
                                )}

                                {/* Client review actions — only on submitted milestones */}
                                {ms.status === 'submitted' && canAct && (
                                  <div className="flex gap-2 mt-3">
                                    <Button
                                      size="sm"
                                      onClick={() => handleApproveMilestone(ms.id)}
                                      disabled={!!actionLoading}
                                      iconName="CheckCircle"
                                      iconPosition="left"
                                    >
                                      {actionLoading === 'Approve milestone' ? 'Approving...' : 'Approve'}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-destructive border-destructive/30 hover:bg-destructive/10"
                                      onClick={() => { setRejectingId(ms.id); setRejectReason(''); }}
                                      iconName="XCircle"
                                      iconPosition="left"
                                    >
                                      Reject
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ════════════ TIMESHEET ════════════ */}
              {activeTab === 'timesheet' && (
                <div className="space-y-5">
                  {/* Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: 'Total Hours',  value: `${totalHours}h`,              icon: 'Clock',       color: 'text-primary',   bg: 'bg-primary/10' },
                      { label: 'Total Billed', value: `$${totalBill.toFixed(2)}`,    icon: 'DollarSign',  color: 'text-foreground',bg: 'bg-muted'      },
                      { label: 'Approved',     value: `$${approvedBill.toFixed(2)}`, icon: 'CheckCircle', color: 'text-success',   bg: 'bg-success/10' },
                    ].map(s => (
                      <div key={s.label} className="bg-background border border-border rounded-xl p-4 flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
                          <Icon name={s.icon} size={16} className={s.color} />
                        </div>
                        <div>
                          <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
                          <p className="text-xs text-muted-foreground">{s.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>


                  {timeEntries.length === 0 ? (
                    <EmptyState icon="Clock" message="The expert hasn't logged any time entries yet." />
                  ) : (
                    <div className="space-y-2">
                      {timeEntries.map(e => (
                        <div key={e.id} className="bg-background border border-border rounded-xl p-4 transition-colors">
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 flex-wrap mb-1">
                                <span className="text-sm font-medium text-foreground">
                                  {new Date(e.work_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                                <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
                                  {(e.minutes / 60).toFixed(1)}h · ${e.hourly_rate?.toFixed(2)}/hr
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">{e.description}</p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="text-base font-bold text-foreground">${e.billable_amount?.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ════════════ PAYMENTS ════════════ */}
              {activeTab === 'payments' && (
                <div className="space-y-5">
                  <div className="bg-background border border-border rounded-xl p-5">
                    <h4 className="text-sm font-semibold text-foreground mb-4">Payment Summary</h4>
                    <div className="divide-y divide-border/50 space-y-0">
                      {c.contract_type === 'hourly' && (
                        <>
                          {[
                            { label: 'Hourly Rate',    value: `$${c.hourly_rate?.toFixed(2)}/hr` },
                            { label: 'Budget Cap',     value: c.budget_cap ? `$${c.budget_cap?.toFixed(2)}` : 'None' },
                            { label: 'Hours Logged',   value: `${totalHours}h` },
                            { label: 'Total Billed',   value: `$${totalBill.toFixed(2)}` },
                          ].map(r => (
                            <div key={r.label} className="flex justify-between py-2.5">
                              <p className="text-sm text-muted-foreground">{r.label}</p>
                              <p className="text-sm font-medium text-foreground">{r.value}</p>
                            </div>
                          ))}
                          <div className="flex justify-between py-2.5 border-t border-border mt-1">
                            <p className="text-sm font-semibold text-foreground">Approved Amount</p>
                            <p className="text-base font-bold text-primary">${approvedBill.toFixed(2)}</p>
                          </div>
                        </>
                      )}
                      {c.contract_type === 'milestone' && (
                        <>
                          {[
                            { label: 'Total Contract Value', value: `$${c.total_contract_value?.toFixed(2)}` },
                            { label: 'Milestones',           value: `${approvedMs} / ${milestones.length} approved` },
                          ].map(r => (
                            <div key={r.label} className="flex justify-between py-2.5">
                              <p className="text-sm text-muted-foreground">{r.label}</p>
                              <p className="text-sm font-medium text-foreground">{r.value}</p>
                            </div>
                          ))}
                          <div className="flex justify-between py-2.5 border-t border-border mt-1">
                            <p className="text-sm font-semibold text-foreground">Approved Value</p>
                            <p className="text-base font-bold text-primary">${approvedMsValue.toFixed(2)}</p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Escrow card */}
                  <div className={`rounded-xl border p-5 ${
                    c.escrow_status === 'released' ? 'border-success/40 bg-success/5' :
                    c.escrow_status === 'held'     ? 'border-warning/40 bg-warning/5' :
                    'border-border bg-background'
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        c.escrow_status === 'released' ? 'bg-success/15' : c.escrow_status === 'held' ? 'bg-warning/15' : 'bg-muted'
                      }`}>
                        <Icon
                          name={c.escrow_status === 'released' ? 'Unlock' : c.escrow_status === 'held' ? 'Lock' : 'CreditCard'}
                          size={18}
                          className={c.escrow_status === 'released' ? 'text-success' : c.escrow_status === 'held' ? 'text-warning' : 'text-muted-foreground'}
                        />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground capitalize">Escrow: {c.escrow_status || 'Not configured'}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {c.escrow_status === 'released' && 'Funds released to the expert after contract completion.'}
                          {c.escrow_status === 'held'     && 'Your payment is securely held in escrow until completion.'}
                          {c.escrow_status === 'refunded' && 'Escrow was refunded to your account.'}
                          {(!c.escrow_status || c.escrow_status === 'none') && 'No escrow configured for this contract.'}
                        </p>
                        {c.escrow_amount > 0 && (
                          <p className="mt-2 text-sm">Held: <span className="font-bold text-foreground">${c.escrow_amount?.toFixed(2)}</span></p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Per-milestone payments table */}
                  {c.contract_type === 'milestone' && milestones.length > 0 && (
                    <div className="bg-background border border-border rounded-xl overflow-hidden">
                      <div className="px-5 py-4 border-b border-border">
                        <h4 className="text-sm font-semibold text-foreground">Milestone Payments</h4>
                      </div>
                      <div className="divide-y divide-border">
                        {milestones.map(ms => (
                          <div key={ms.id} className="flex items-center justify-between px-5 py-3 gap-4">
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-foreground truncate">{ms.title}</p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${MILESTONE_CHIP[ms.status] || 'bg-muted text-muted-foreground'}`}>
                                {ms.status?.replace('_', ' ')}
                              </span>
                              <p className="text-sm font-bold text-foreground w-20 text-right">${ms.amount?.toFixed(2)}</p>
                            </div>
                          </div>
                        ))}
                        <div className="flex justify-between px-5 py-3 bg-muted/30">
                          <p className="text-sm font-semibold text-foreground">Total</p>
                          <p className="text-sm font-bold text-foreground">${milestones.reduce((s, m) => s + (m.amount || 0), 0).toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>

    {/* Review Modal — on close/submit, open tip modal as next step if not already tipped */}
    <ReviewModal
      expert={c.expert ? { id: c.expert_id, full_name: c.expert.full_name, title: c.expert.title, avatar_url: c.expert.avatar_url } : null}
      contractId={c.id}
      isOpen={showReviewModal}
      onClose={() => {
        setShowReviewModal(false);
        if (!alreadyTipped) setShowTipModal(true);
      }}
      onSubmitted={() => {
        setAlreadyReviewed(true);
        loadDetails();
        setShowReviewModal(false);
        if (!alreadyTipped) setShowTipModal(true);
      }}
    />
    {/* Tip Modal */}
    <TipModal
      expert={c.expert ? { id: c.expert_id, full_name: c.expert.full_name, title: c.expert.title, avatar_url: c.expert.avatar_url } : null}
      contractId={c.id}
      isOpen={showTipModal}
      onClose={() => setShowTipModal(false)}
      onSubmitted={() => {
        setAlreadyTipped(true);
        toast.success(`Tip sent to ${c.expert?.full_name || 'the expert'}! 🎉`);
      }}
    />
  </>
  );
};

export default ClientContractModal;
